/**
 * RevenueByProviderChart — gestapelter Tagesumsatz nach Zahlungsplattform.
 *
 * Aufbau bewusst als Klon von features/hotspots/HotspotRewardsChart (recharts
 * BarChart in ResponsiveContainer, interaktive Legende ueber ein hiddenSeries-Set),
 * nur gestapelt statt gruppiert: bei sechs Plattformen ist die Tagessumme die
 * eigentliche Aussage, die Aufteilung die Nebeninformation.
 *
 * Der Legacy-Screen rechnete diese Reihe mit rund 427 Einzelqueries; hier kommt
 * sie fertig aggregiert aus einer einzigen Backend-Abfrage.
 */
import { useState, useCallback, useMemo } from 'react'
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
} from 'recharts'
import { useTranslation } from 'react-i18next'
import type { AdminRevenueSeries } from './useAdminRevenue'
import { providerColor, fmtUsd } from './revenueFormat'

/** '2026-07-19' -> '19.07' */
function shortDate(dateStr: string): string {
  const parts = dateStr.split('-')
  if (parts.length !== 3) return dateStr
  return `${parts[2]}.${parts[1]}`
}

type ChartRow = { date: string; total: number } & Record<string, string | number>

type TooltipProps = {
  active?: boolean
  payload?: Array<{ payload?: ChartRow }>
}

function ChartTooltip({ active, payload }: TooltipProps) {
  const { t } = useTranslation('adminRevenue')
  const row = payload?.[0]?.payload
  if (!active || !row) return null
  return (
    <div className="card card-pad" style={{ fontSize: 12, lineHeight: 1.6 }}>
      <div style={{ fontWeight: 600 }}>{row.date}</div>
      <div className="tnum">{fmtUsd(row.total)}</div>
      <div className="muted">{t('kpi.transactions', { count: Number(row.count ?? 0) })}</div>
    </div>
  )
}

type LegendItemProps = {
  id: string
  label: string
  color: string
  visible: boolean
  onToggle: (id: string) => void
}

function LegendItem({ id, label, color, visible, onToggle }: LegendItemProps) {
  const { t } = useTranslation('adminRevenue')
  const toggleLabel = visible ? t('chart.toggleHide') : t('chart.toggleShow')
  return (
    <button
      type="button"
      onClick={() => onToggle(id)}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        padding: '4px 8px',
        background: 'transparent',
        border: 'none',
        cursor: 'pointer',
        opacity: visible ? 1 : 0.4,
        transition: 'opacity 0.2s',
        borderRadius: 'var(--radius-sm)',
      }}
      aria-pressed={visible}
      title={`${toggleLabel} ${label}`}
      data-testid={`revenue-legend-${id}`}
    >
      <span style={{ width: 10, height: 10, borderRadius: 2, background: color, flexShrink: 0 }} />
      <span style={{ fontSize: 12, color: 'var(--text)' }}>{label}</span>
    </button>
  )
}

type Props = {
  series: AdminRevenueSeries
}

export function RevenueByProviderChart({ series }: Props) {
  const { t } = useTranslation('adminRevenue')
  const [hiddenSeries, setHiddenSeries] = useState<Set<string>>(new Set())

  const toggleSeries = useCallback((seriesId: string) => {
    setHiddenSeries((prev) => {
      const next = new Set(prev)
      if (next.has(seriesId)) next.delete(seriesId)
      else next.add(seriesId)
      return next
    })
  }, [])

  /**
   * recharts braucht je Serie einen eigenen Key auf der Zeile; die Punkte aus
   * dem Backend sind duenn besetzt, fehlende Plattformen werden hier zu 0.
   */
  const chartData = useMemo<ChartRow[]>(
    () =>
      series.points.map((point) => {
        const row: ChartRow = { date: point.date, total: point.total, count: point.count }
        for (const provider of series.providers) {
          row[provider] = point.providers[provider] ?? 0
        }
        return row
      }),
    [series],
  )

  const hasRevenue = series.points.some((point) => point.total > 0)

  return (
    <div className="card card-pad" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <h3 className="section-heading" style={{ marginBottom: 0 }}>
        {t('chart.heading', { days: series.days })}
      </h3>

      {!hasRevenue ? (
        <div
          style={{ height: 250, display: 'grid', placeItems: 'center' }}
          data-testid="revenue-chart-empty"
        >
          <span className="muted">{t('chart.noData')}</span>
        </div>
      ) : (
        <>
          <div
            style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}
            role="group"
            aria-label={t('chart.seriesToggleLabel')}
          >
            {series.providers.map((provider) => (
              <LegendItem
                key={provider}
                id={provider}
                label={t(`providers.${provider}`, { defaultValue: provider.toUpperCase() })}
                color={providerColor(provider)}
                visible={!hiddenSeries.has(provider)}
                onToggle={toggleSeries}
              />
            ))}
          </div>

          <div aria-label={t('chart.chartLabel')} style={{ marginTop: 8 }}>
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={chartData} margin={{ top: 4, right: 8, left: -20, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false} />
                <XAxis
                  dataKey="date"
                  tickFormatter={shortDate}
                  tick={{ fontSize: 10, fill: 'var(--text-muted)' }}
                  tickLine={false}
                  axisLine={false}
                  minTickGap={20}
                />
                <YAxis
                  tick={{ fontSize: 10, fill: 'var(--text-muted)' }}
                  tickFormatter={(v: number) => v.toFixed(0)}
                  tickLine={false}
                  axisLine={false}
                  domain={['auto', 'auto']}
                />
                <Tooltip content={<ChartTooltip />} cursor={{ fill: 'var(--border)', opacity: 0.2 }} />
                {series.providers.map((provider) => (
                  <Bar
                    key={provider}
                    dataKey={provider}
                    stackId="revenue"
                    fill={providerColor(provider)}
                    hide={hiddenSeries.has(provider)}
                    name={provider.toUpperCase()}
                  />
                ))}
              </BarChart>
            </ResponsiveContainer>
          </div>
        </>
      )}
    </div>
  )
}
